const targetProfiles = {
  "Property Managers": {
    roles: ["Operations Manager", "Managing Director", "Portfolio Lead"],
    companies: [
      "UrbanNest Property Group",
      "Kilele Homes Management",
      "MetroLease Partners",
      "Acacia Estate Services",
      "Nairobi Property Desk",
    ],
    domains: ["urbannest.co.ke", "kilelehomes.co.ke", "metrolease.africa", "acaciaestate.co.ke", "nairobipropertydesk.com"],
    signals: [
      "Manages multiple rental units across different locations",
      "Likely coordinating rent collection, maintenance, and tenant communication manually",
      "Needs clearer portfolio reporting for owners and managers",
    ],
    pain: "fragmented property operations",
  },
  "Real Estate Developers": {
    roles: ["Commercial Director", "Sales Operations Lead", "Founder"],
    companies: [
      "Keystone Urban Developments",
      "Jenga Living Projects",
      "PrimeBlock Residences",
      "Northline Developments",
      "CivicSpace Homes",
    ],
    domains: ["keystoneurban.co.ke", "jengaliving.com", "primeblock.co.ke", "northline.africa", "civicspacehomes.com"],
    signals: [
      "Launching or managing new residential inventory",
      "Needs smoother handover from sales to property operations",
      "Owner reporting and tenant onboarding can become difficult as units scale",
    ],
    pain: "post-handover operating visibility",
  },
  "Coworking Operators": {
    roles: ["Community Manager", "Operations Lead", "General Manager"],
    companies: [
      "HiveDesk Studios",
      "Workyard Collective",
      "Orbit Offices",
      "Mtaa Workspace",
      "LaunchBay Nairobi",
    ],
    domains: ["hivedesk.africa", "workyardcollective.com", "orbitoffices.co.ke", "mtaaworkspace.com", "launchbay.ke"],
    signals: [
      "Handles recurring member billing and service requests",
      "Needs quick visibility into occupancy and account follow-up",
      "Communication history matters for renewals and support",
    ],
    pain: "member billing and service coordination",
  },
  "Serviced Apartments": {
    roles: ["Property Operations Manager", "Guest Experience Lead", "General Manager"],
    companies: [
      "Staywell Suites",
      "Nomad Residence Group",
      "Amani Executive Stays",
      "VistaStay Apartments",
      "Harbor Living Suites",
    ],
    domains: ["staywellsuites.co.ke", "nomadresidence.africa", "amanistays.com", "vistastay.co.ke", "harborlivingsuites.com"],
    signals: [
      "Combines accommodation operations with maintenance and payments",
      "Needs timely guest/resident communication",
      "Recurring stays and unit status require tighter tracking",
    ],
    pain: "unit status and guest communication",
  },
};

const replyRules = [
  {
    intent: "Interested",
    status: "Reply - Interested",
    confidence: 94,
    nextStep: "Send a short scheduling reply and offer a demo slot.",
    keywords: ["interested", "demo", "call", "meeting", "schedule", "pricing", "learn more"],
  },
  {
    intent: "Question",
    status: "Reply - Needs Answer",
    confidence: 88,
    nextStep: "Answer the question directly, then invite them to a short walkthrough.",
    keywords: ["how", "what", "price", "cost", "integrate", "features", "can it", "does it"],
  },
  {
    intent: "Not Interested",
    status: "Closed - Not Interested",
    confidence: 91,
    nextStep: "Acknowledge politely and stop follow-up for this lead.",
    keywords: ["not interested", "no thanks", "stop", "unsubscribe", "remove me", "not now"],
  },
  {
    intent: "Out of Office",
    status: "Reply - Later",
    confidence: 84,
    nextStep: "Schedule a follow-up after the return date if one is provided.",
    keywords: ["out of office", "away", "annual leave", "back on", "returning"],
  },
];

function titleCase(value) {
  return String(value || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`)
    .join(" ");
}

function normalizeLocation(value) {
  return titleCase(value || "Nairobi");
}

function emailFromName(name, domain) {
  const first = String(name || "contact").split(" ")[0].toLowerCase().replace(/[^a-z]/g, "");
  return `${first || "hello"}@${domain}`;
}

function leadScore(index, segment, location) {
  const base = 72 + ((index * 7 + segment.length + location.length) % 19);
  return Math.min(base, 96);
}

function scoreTier(score) {
  if (score >= 88) return "High fit";
  if (score >= 78) return "Good fit";
  return "Research more";
}

function pick(list, index) {
  return list[index % list.length];
}

function splitLeadRow(row) {
  return String(row || "")
    .split(/\t|,/)
    .map((value) => value.trim().replace(/^"|"$/g, ""));
}

function slugId(value, index) {
  const slug = String(value || "lead")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);

  return `lead-import-${slug || "lead"}-${Date.now()}-${index}`;
}

export function buildResearchLeads({ segment, location, offer, count = 5 }) {
  const profile = targetProfiles[segment] || targetProfiles["Property Managers"];
  const market = normalizeLocation(location);

  return Array.from({ length: count }).map((_, index) => {
    const company = pick(profile.companies, index);
    const domain = pick(profile.domains, index);
    const role = pick(profile.roles, index);
    const contactName = pick(["Grace Wanjiku", "Daniel Otieno", "Aisha Mwangi", "Brian Kamau", "Miriam Njeri"], index);
    const score = leadScore(index, segment, market);

    return {
      id: `lead-${Date.now()}-${index}`,
      company,
      contactName,
      role,
      email: emailFromName(contactName, domain),
      website: `https://${domain}`,
      segment,
      location: market,
      score,
      scoreTier: scoreTier(score),
      status: "Research",
      source: "Agent research queue",
      fitReason: `${company} matches SpaceM8 because it appears aligned with ${profile.pain} in ${market}.`,
      signals: profile.signals,
      recommendedOffer: offer || "a short SpaceM8 walkthrough focused on rent, maintenance, and communication workflows",
      lastUpdated: new Date().toISOString(),
      draft: "",
      replyText: "",
      replyAnalysis: null,
      replyDraft: "",
    };
  });
}

export function buildImportedLeads({ rawText, campaign, existingCount = 0 }) {
  const rows = String(rawText || "")
    .split(/\r?\n/)
    .map((row) => row.trim())
    .filter(Boolean);

  if (!rows.length) return [];

  const firstRow = splitLeadRow(rows[0]).map((value) => value.toLowerCase());
  const hasHeader = firstRow.some((value) =>
    ["company", "contact", "contact name", "name", "role", "email", "website", "notes"].includes(value)
  );
  const headers = hasHeader ? firstRow : [];
  const dataRows = hasHeader ? rows.slice(1) : rows;

  const valueAt = (values, keys, fallbackIndex) => {
    const headerIndex = keys
      .map((key) => headers.indexOf(key))
      .find((index) => index >= 0);
    return values[headerIndex >= 0 ? headerIndex : fallbackIndex] || "";
  };

  return dataRows
    .map((row, index) => {
      const values = splitLeadRow(row);
      const company = valueAt(values, ["company"], 0);
      const contactName = valueAt(values, ["contact name", "contact", "name"], 1) || "Decision Maker";
      const role = valueAt(values, ["role", "title"], 2) || "Operations Lead";
      const email = valueAt(values, ["email", "email address"], 3);
      const website = valueAt(values, ["website", "site", "url"], 4);
      const notes = valueAt(values, ["notes", "signal", "signals"], 5);

      if (!company || !email) return null;

      const score = Math.min(82 + ((index + existingCount) % 12), 94);
      const lead = {
        id: slugId(`${company}-${email}`, index + existingCount),
        company,
        contactName,
        role,
        email,
        website: website || "",
        segment: campaign.segment || "Property Managers",
        location: normalizeLocation(campaign.location),
        score,
        scoreTier: scoreTier(score),
        status: "Research",
        source: "Imported lead list",
        fitReason: notes || `${company} was imported for outreach and should be reviewed against the current campaign brief.`,
        signals: notes ? [notes] : ["Imported lead; review website and role before approving outreach."],
        recommendedOffer: campaign.offer || "a short SpaceM8 walkthrough focused on property operations",
        lastUpdated: new Date().toISOString(),
        draft: "",
        replyText: "",
        replyAnalysis: null,
        replyDraft: "",
      };

      return {
        ...lead,
        draft: buildOutreachDraft(lead, campaign),
      };
    })
    .filter(Boolean);
}

export function buildOutreachDraft(lead, campaign) {
  const senderName = campaign.senderName || "SpaceM8 Team";
  const offer = lead.recommendedOffer || campaign.offer || "a quick SpaceM8 walkthrough";
  const signal = lead.signals?.[0] || "your property operations may be growing";

  return [
    `Subject: Cleaner property operations for ${lead.company}`,
    "",
    `Hi ${lead.contactName.split(" ")[0]},`,
    "",
    `I noticed ${lead.company} is a strong fit for teams handling ${lead.segment.toLowerCase()} in ${lead.location}. ${signal}.`,
    "",
    `SpaceM8 helps teams keep properties, rent follow-ups, maintenance, documents, and communication in one operating dashboard. I thought ${offer} could be useful for your team.`,
    "",
    `Would you be open to a 15 minute conversation this week to see if it fits ${lead.company}?`,
    "",
    `Best,`,
    senderName,
  ].join("\n");
}

export function analyzeReply(replyText) {
  const normalized = String(replyText || "").toLowerCase();
  const matchedRule = replyRules.find((rule) =>
    rule.keywords.some((keyword) => normalized.includes(keyword))
  );

  if (matchedRule) {
    return {
      intent: matchedRule.intent,
      status: matchedRule.status,
      confidence: matchedRule.confidence,
      nextStep: matchedRule.nextStep,
    };
  }

  return {
    intent: "Needs Review",
    status: "Reply - Manual Review",
    confidence: 63,
    nextStep: "Review the reply manually before sending a response.",
  };
}

export function buildReplyDraft(lead, analysis, campaign) {
  const firstName = lead.contactName.split(" ")[0];
  const senderName = campaign.senderName || "SpaceM8 Team";
  const schedulingLink = campaign.schedulingLink || "your preferred time";

  if (analysis.intent === "Interested") {
    return [
      `Hi ${firstName},`,
      "",
      "Thanks for getting back to me. A short walkthrough would be the best next step.",
      "",
      `You can pick a time here: ${schedulingLink}. I can focus the demo on lead-to-lease operations, rent follow-up, maintenance tracking, and owner reporting.`,
      "",
      `Best,`,
      senderName,
    ].join("\n");
  }

  if (analysis.intent === "Question") {
    return [
      `Hi ${firstName},`,
      "",
      "Good question. SpaceM8 is built to centralize property records, tenants, payments, maintenance, documents, reports, and communication so the team can manage the portfolio from one dashboard.",
      "",
      "If helpful, I can show the exact workflow in a short walkthrough and answer pricing or setup questions based on your portfolio size.",
      "",
      `Best,`,
      senderName,
    ].join("\n");
  }

  if (analysis.intent === "Not Interested") {
    return [
      `Hi ${firstName},`,
      "",
      "Thanks for letting me know. I will close the loop here and will not follow up further.",
      "",
      `Best,`,
      senderName,
    ].join("\n");
  }

  if (analysis.intent === "Out of Office") {
    return [
      `Hi ${firstName},`,
      "",
      "Thanks for the note. I will follow up later when you are back.",
      "",
      `Best,`,
      senderName,
    ].join("\n");
  }

  return [
    `Hi ${firstName},`,
    "",
    "Thanks for your reply. I wanted to respond carefully, so I will review your note and come back with the right next step.",
    "",
    `Best,`,
    senderName,
  ].join("\n");
}

export function buildMailtoUrl(lead, draft) {
  const [subjectLine, ...bodyLines] = String(draft || "").split("\n");
  const subject = subjectLine.replace(/^subject:\s*/i, "").trim() || "SpaceM8 follow-up";
  const body = bodyLines.join("\n").trim();
  return `mailto:${encodeURIComponent(lead.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function summarizeOutreach(leads) {
  const leadList = leads || [];
  return {
    total: leadList.length,
    highFit: leadList.filter((lead) => lead.score >= 88).length,
    drafts: leadList.filter((lead) => Boolean(lead.draft)).length,
    replies: leadList.filter((lead) => Boolean(lead.replyAnalysis)).length,
    queued: leadList.filter((lead) => lead.status === "Queued").length,
  };
}
